/**
 * The product surface a given LLM call belongs to, recorded on every
 * `llm_usage` row.
 *
 * Several features share the same two models, so the model alone cannot say
 * where spend is going — this label is what makes the admin breakdown
 * actionable. Values are stable identifiers: renaming one splits its history
 * in the usage table, so add a new entry rather than editing an existing value.
 */
export const LLM_USAGE_FEATURES = {
  ScriptGeneration: 'script-generation',
  ScriptEdit: 'script-edit',
  TopicRecommendations: 'topic-recommendations',
  SetGeneration: 'set-generation',
  StoryboardBeats: 'storyboard-beats',
  StoryboardImage: 'storyboard-image',
  ScriptReview: 'script-review',
  ScriptMatch: 'script-match',
  ScriptExtraction: 'script-extraction',
  PartnerVoiceoverCheck: 'partner-voiceover-check',
  RejectionSuggestion: 'rejection-suggestion',
  PersonaAvatar: 'persona-avatar',
  PersonaScriptGeneration: 'persona-script-generation',
  PersonaScriptImport: 'persona-script-import',
  PersonaBriefGeneration: 'persona-brief-generation',
  PersonaBriefEdit: 'persona-brief-edit',
  ChangelogNotes: 'changelog-notes',
  ChangelogDigest: 'changelog-digest',
  PromptCatalog: 'prompt-catalog',
} as const;

export type LlmUsageFeature = (typeof LLM_USAGE_FEATURES)[keyof typeof LLM_USAGE_FEATURES];

export const LLM_USAGE_FEATURE_VALUES = Object.values(LLM_USAGE_FEATURES) as LlmUsageFeature[];

export function isLlmUsageFeature(value: string): value is LlmUsageFeature {
  return (LLM_USAGE_FEATURE_VALUES as string[]).includes(value);
}

/**
 * The Prompt Manager `usedIn` a feature's calls are made for, so Token Usage
 * can link a row to the prompt that would actually change.
 *
 * A feature with several prompts points at the one an admin would switch
 * first. Features with no prompt (the catalog) are absent.
 */
export const FEATURE_TO_USED_IN: Partial<Record<LlmUsageFeature, string>> = {
  [LLM_USAGE_FEATURES.ScriptGeneration]: 'creative-script-generation-angles',
  [LLM_USAGE_FEATURES.ScriptEdit]: 'creative-script-generation-custom',
  [LLM_USAGE_FEATURES.TopicRecommendations]: 'creative-script-generation-topics',
  [LLM_USAGE_FEATURES.SetGeneration]: 'opportunity-generation-premise',
  [LLM_USAGE_FEATURES.StoryboardBeats]: 'creative-storyboard-beat-extraction',
  [LLM_USAGE_FEATURES.StoryboardImage]: 'creative-storyboard-image',
  [LLM_USAGE_FEATURES.PersonaAvatar]: 'persona-avatar-generation',
  [LLM_USAGE_FEATURES.PersonaScriptGeneration]: 'creative-script-generation-persona-angles',
  [LLM_USAGE_FEATURES.PersonaBriefGeneration]: 'creative-brief-generation-persona-angles',
  [LLM_USAGE_FEATURES.PersonaBriefEdit]: 'creative-brief-edit-persona-angles',
  [LLM_USAGE_FEATURES.PersonaScriptImport]: 'creative-script-import-persona-structure',
  [LLM_USAGE_FEATURES.ChangelogNotes]: 'changelog.deploy-notes',
  [LLM_USAGE_FEATURES.ChangelogDigest]: 'changelog.roundup',
};

const IMAGE_FEATURES = new Set<LlmUsageFeature>([
  LLM_USAGE_FEATURES.StoryboardImage,
  LLM_USAGE_FEATURES.PersonaAvatar,
]);

export function isImageUsageFeature(feature: string | null | undefined): boolean {
  return feature != null && IMAGE_FEATURES.has(feature as LlmUsageFeature);
}
